/**
 * Shared, XSS-hardened product-card rendering for every search surface
 * (semantic-search.js on /search, visual-similar.js on product pages).
 *
 * Why this file exists: card building is security-critical — it is what keeps a
 * product title or URL from breaking out of an attribute and injecting markup.
 * Copied per feature, a future fix to one copy silently misses the others, and
 * the failure mode is an XSS hole rather than a visible bug. One copy, used
 * everywhere.
 *
 * Rules (do not "simplify" these away):
 *   - Build with DOM APIs only: createElement + textContent / setAttribute, which
 *     encode text- and attribute-context safely. NEVER innerHTML string
 *     concatenation with product data.
 *   - Every URL goes through safeUrl() so javascript:/data:/vbscript: can never
 *     reach an href/src.
 *
 * Loaded before its consumers; both use defer, which preserves document order.
 * Exposes window.DubistdaSearch.
 */
(function () {
  function currentLang() {
    var htmlLang = (document.documentElement.getAttribute('lang') || 'de').toLowerCase();
    return htmlLang.indexOf('de') === 0 ? 'de' : 'en';
  }

  /**
   * Shopify's product JSON serialises CDN image URLs as `//cdn.shopify.com/...`.
   * Accept that one credential-free host by converting it to explicit HTTPS.
   * Every other scheme-relative URL stays forbidden.
   */
  function normalizeShopifyCdnUrl(u) {
    return /^\/\/cdn\.shopify\.com\//i.test(u) ? 'https:' + u : u;
  }

  // Allow only same-origin relative paths or explicit http(s) URLs — blocks
  // javascript:/data:/vbscript: and arbitrary scheme-relative cross-origin URLs
  // from ever reaching an href/src attribute.
  function safeUrl(u) {
    if (typeof u !== 'string' || !u) return '';
    u = normalizeShopifyCdnUrl(u);
    if (u.charAt(0) === '/' && u.charAt(1) !== '/' && u.charAt(1) !== '\\') return u;
    if (!/^https?:\/\//i.test(u)) return '';
    try {
      var parsed = new URL(u);
      if (
        (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
        !parsed.username &&
        !parsed.password
      ) return u;
    } catch (error) {
      return '';
    }
    return '';
  }

  function str(s) {
    return s == null ? '' : String(s);
  }

  /**
   * Resolve a storefront-relative path through Shopify's locale-aware root.
   * On a German-primary store the root is "/", while an English market may be
   * "/en/". Hard-coding either form makes the other market leave preview mode
   * or request the wrong product JSON endpoint.
   */
  function storefrontRoot() {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) ||
      '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function storefrontPath(path) {
    return storefrontRoot() + str(path).replace(/^\/+/, '');
  }

  function storefrontUrl(url) {
    var safe = safeUrl(url);
    if (!safe || safe.charAt(0) !== '/') return safe;
    var root = storefrontRoot();
    if (root === '/' || safe === root.slice(0, -1) || safe.indexOf(root) === 0) return safe;
    return root + safe.replace(/^\/+/, '');
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency,
      });
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  // Reuses Dawn's card classes for typography. See the XSS rules above.
  function buildCard(product) {
    var li = document.createElement('li');
    li.className = 'grid__item semantic-search__item';
    var url = storefrontUrl(product.url);
    var image = safeUrl(product.featured_image || (product.images && product.images[0]) || '');
    var title = str(product.title);
    var price = formatMoney(product.price);

    var wrapper = document.createElement('div');
    wrapper.className = 'card-wrapper product-card-wrapper underline-links-hover';

    var imgLink = document.createElement('a');
    imgLink.className = 'full-unstyled-link semantic-search__media-link';
    imgLink.setAttribute('href', url || '#');
    imgLink.setAttribute('aria-label', title);
    var media = document.createElement('span');
    media.className = 'semantic-search__media';
    var placeholder = document.createElement('span');
    placeholder.className = 'semantic-search__media-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    media.appendChild(placeholder);
    if (image) {
      var img = document.createElement('img');
      img.setAttribute('src', image);
      img.setAttribute('alt', title);
      // Search results are primary content, not below-the-fold decoration. These
      // cards are inserted by JS after paint, so native loading="lazy" left them
      // flashing the blank placeholder until a scroll/paint tick — eager + high
      // priority makes the product image appear with the card.
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
      img.setAttribute('width', '400');
      img.setAttribute('height', '400');
      img.className = 'motion-reduce';
      img.addEventListener('error', function () {
        img.remove();
      });
      media.appendChild(img);
    }
    imgLink.appendChild(media);
    wrapper.appendChild(imgLink);

    var content = document.createElement('div');
    content.className = 'card__content';
    var info = document.createElement('div');
    info.className = 'card__information';

    var h3 = document.createElement('h3');
    h3.className = 'card__heading h5';
    var titleLink = document.createElement('a');
    titleLink.className = 'full-unstyled-link';
    titleLink.setAttribute('href', url || '#');
    titleLink.textContent = title;
    h3.appendChild(titleLink);
    info.appendChild(h3);

    if (price) {
      var priceDiv = document.createElement('div');
      priceDiv.className = 'price';
      var priceSpan = document.createElement('span');
      priceSpan.className = 'price-item';
      priceSpan.textContent = price;
      priceDiv.appendChild(priceSpan);
      info.appendChild(priceDiv);
    }

    content.appendChild(info);
    wrapper.appendChild(content);
    li.appendChild(wrapper);
    return li;
  }

  function fetchWithTimeout(url, ms, options) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, ms);
    var opts = options || {};
    opts.signal = controller.signal;
    return fetch(url, opts).finally(function () {
      clearTimeout(timer);
    });
  }

  /**
   * Hydrate search-service results (which carry only handle/score) into full
   * Shopify product objects via the storefront's own /products/<handle>.js.
   * Returns [] rather than throwing — a search surface must never break the page.
   */
  async function hydrate(results) {
    try {
      var products = await Promise.all(
        results.map(function (r) {
          return fetch(storefrontPath('products/' + encodeURIComponent(r.handle) + '.js'))
            .then(function (res) {
              return res.ok ? res.json() : null;
            })
            .catch(function () {
              return null;
            });
        })
      );
      return products.filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  window.DubistdaSearch = {
    API_BASE: 'https://search.a2-labs.ai',
    currentLang: currentLang,
    storefrontPath: storefrontPath,
    storefrontUrl: storefrontUrl,
    safeUrl: safeUrl,
    str: str,
    formatMoney: formatMoney,
    buildCard: buildCard,
    fetchWithTimeout: fetchWithTimeout,
    hydrate: hydrate,
  };
})();
